import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { Store, MapPin, ChevronRight, Search } from 'lucide-react';
import { Input } from '../../components/ui/input';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StoreSelectPage = () => {
  const navigate = useNavigate();
  const { registerWithStore, user } = useAuth();
  
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    // If user already has a store, redirect
    if (user?.admin_id) {
      navigate('/');
      return;
    }

    // If we know the tenant from the URL/slug, auto-register instead of showing the list
    const autoRegister = async () => {
      const { resolveTenantSlug } = await import('../../utils/tenantResolver');
      const slug = resolveTenantSlug();
      if (slug) {
        try {
          const settingsRes = await axios.get(`${API}/store/by-slug/${slug}`);
          const tenantAdminId = settingsRes.data?.admin_id;
          if (tenantAdminId) {
            setRegistering(true);
            await registerWithStore(tenantAdminId);
            navigate('/');
            return;
          }
        } catch { /* slug not found — show store list */ }
      }
      loadStores();
    };
    autoRegister();
  }, [user, navigate, registerWithStore]);

  const loadStores = async () => {
    try {
      const response = await axios.get(`${API}/stores`);
      setStores(response.data);
    } catch (error) {
      console.error('Failed to load stores:', error);
      toast.error('Failed to load stores');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectStore = async (storeId) => {
    setRegistering(true);
    try {
      await registerWithStore(storeId);
      toast.success('Registered successfully!');
      navigate('/');
    } catch (error) {
      toast.error('Failed to register with store');
    } finally {
      setRegistering(false);
    }
  };

  const filteredStores = stores.filter(store =>
    store.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getThemeColor = (theme) => {
    const colors = {
      'theme-modern-organic': '#2E3B32',
      'theme-country': '#1B5E20',
      'theme-zepto': '#4A148C',
      'theme-instamart': '#E65100',
      'theme-blinkit': '#FBC02D',
    };
    return colors[theme] || colors['theme-modern-organic'];
  };

  return (
    <div className="min-h-screen bg-[var(--theme-background)] p-4">
      <div className="max-w-lg mx-auto pt-8">
        {/* Header */}
        <div className="text-center mb-8 animate-fade-in">
          <div className="w-16 h-16 rounded-2xl bg-[var(--theme-primary)] flex items-center justify-center mx-auto mb-4">
            <MapPin className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-[var(--theme-text-main)]">
            Select Your Store
          </h1>
          <p className="text-[var(--theme-text-muted)] mt-2">
            Choose a store near you to start ordering
          </p>
        </div>

        {/* Search */}
        <div className="relative mb-6 animate-fade-in">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--theme-text-muted)]" />
          <Input
            type="text"
            placeholder="Search stores..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-12 input-modern"
            data-testid="store-search-input"
          />
        </div>

        {/* Stores List */}
        <div className="space-y-4">
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-[var(--theme-surface)] rounded-xl p-4 animate-pulse">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl bg-gray-200"></div>
                    <div className="flex-1">
                      <div className="h-5 bg-gray-200 rounded w-1/2 mb-2"></div>
                      <div className="h-4 bg-gray-100 rounded w-1/3"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : filteredStores.length === 0 ? (
            <div className="text-center py-12 animate-fade-in">
              <Store className="w-16 h-16 text-[var(--theme-text-muted)] mx-auto mb-4 opacity-50" />
              <p className="text-[var(--theme-text-muted)]">
                {searchQuery ? 'No stores found matching your search' : 'No stores available yet'}
              </p>
              <p className="text-sm text-[var(--theme-text-muted)] mt-2">
                Please check back later or contact support
              </p>
            </div>
          ) : (
            filteredStores.map((store, index) => (
              <button
                key={store.id}
                onClick={() => handleSelectStore(store.id)}
                disabled={registering}
                className={`w-full bg-[var(--theme-surface)] rounded-xl p-4 border border-black/5 hover:border-[var(--theme-primary)]/50 transition-all hover:shadow-lg text-left animate-fade-in stagger-${index + 1}`}
                data-testid={`store-${store.id}`}
              >
                <div className="flex items-center gap-4">
                  <div 
                    className="w-14 h-14 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: getThemeColor(store.theme) }}
                  >
                    {store.logo ? (
                      <img src={store.logo} alt={store.name} className="w-10 h-10 object-contain" />
                    ) : (
                      <Store className="w-7 h-7 text-white" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading font-bold text-[var(--theme-text-main)] truncate">
                      {store.name}
                    </h3>
                    <p className="text-sm text-[var(--theme-text-muted)]">
                      Fresh groceries delivered daily
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-[var(--theme-text-muted)]" />
                </div>
              </button>
            ))
          )}
        </div>

        {/* Demo Store Info */}
        {stores.length === 0 && !loading && (
          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-4 text-center animate-fade-in">
            <p className="text-sm text-blue-800">
              <strong>Tip:</strong> Login as Admin to create a store first, then customers can select it here.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default StoreSelectPage;
